import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  Calendar,
  CheckCircle,
  ChevronRight,
  Clock,
  FileText,
  MapPin,
  Play,
  Shield,
  Star,
  Target,
  TrendingUp
} from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';

// Services
import { learnerLearningPathService, CourseProgress, CourseInPath } from '@/services/learner-learning-path.service';
import apiClient from '@/services/api';
import { API_ENDPOINTS } from '@/config/api.config';
import { useAuth } from '@/contexts/AuthContext';
import ReviewsList from '@/components/reviews/ReviewsList';
import { ReviewType } from '@/types/review.types';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNER_LEARNING_PATHS_PERMISSION } from '@/constants/constant-permissions';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';

interface LearningPathDetailPageProps {
  isLearner?: boolean;
}

export default function LearningPathDetailPage({ isLearner = false }: LearningPathDetailPageProps) {
  const { learning_path_id } = useParams<{ learning_path_id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Permission guards
  const hasWritePermission = useHasPermission([LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE]);
  const canWrite = !isLearner || hasWritePermission;

  // Scroll to top on mount / ID change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [learning_path_id]);

  // Redirect learners without any permission to access-denied
  // Read sessionStorage directly to avoid race condition with useHasPermission's useState(false) initial value
  useEffect(() => {
    if (!isLearner) return;
    const permissions: string[] = JSON.parse(sessionStorage.getItem('permissions') || '[]');
    const hasPermission = permissions.some(p =>
      p === LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_READ ||
      p === LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE
    );
    if (!hasPermission) {
      navigate('/access-denied', { replace: true });
    }
  }, [isLearner, navigate]);

  // Get initial tab from URL query param or default to 'overview'
  const initialTab = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [accessVerified, setAccessVerified] = useState(!isLearner); // Skip verification for admin
  const [accessDenied, setAccessDenied] = useState(false);
  const accessCheckDone = useRef(false);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'courses', label: 'Courses', icon: FileText },
    { id: 'outcomes', label: 'Outcomes', icon: Target },
    { id: 'reviews', label: 'Reviews', icon: Star }
  ];

  // Verify resource access before loading learning path details (only for learners)
  const verifyAccess = useCallback(async () => {
    if (!learning_path_id || !isLearner || accessCheckDone.current) return;

    accessCheckDone.current = true;

    try {
      await apiClient.post(API_ENDPOINTS.AUTH.VERIFY_RESOURCE_ACCESS, {
        learning_path_id: learning_path_id
      });
      setAccessVerified(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorCode = error.response?.data?.error;
        if (errorCode === 'ACCESS_004' || error.response?.status === 403) {
          setAccessDenied(true);
          return;
        }
      }
      // For other errors, allow access and let the page handle it
      setAccessVerified(true);
    }
  }, [learning_path_id, isLearner]);

  // First verify access when component mounts (only for learners)
  useEffect(() => {
    if (isLearner) {
      verifyAccess();
    }
  }, [isLearner, verifyAccess]);

  // Disable body scroll when access is denied
  useEffect(() => {
    if (accessDenied) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [accessDenied]);

  // Fetch learning path details (only after access is verified)
  const { data: learningPath, isLoading, error } = useQuery({
    queryKey: ['learning-path-detail', learning_path_id],
    queryFn: () => learnerLearningPathService.getLearningPathDetail(learning_path_id!),
    enabled: !!learning_path_id && accessVerified,
  });

  if (error) {
    toast.error('Failed to load learning path details');
  }

  // Show skeleton while verifying access (only for learners)
  if (isLearner && !accessVerified && !accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SkeletonLoader type="custom" className="w-full h-40 sm:h-56 md:h-80 lg:h-96 rounded-none" />
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8">
              <div className="flex-1 space-y-4">
                <SkeletonLoader type="custom" className="h-4 w-36 rounded" />
                <SkeletonLoader type="custom" className="h-8 w-3/4 rounded" />
                <div className="flex gap-4">
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-20 rounded" />
                </div>
              </div>
              <div className="lg:flex-shrink-0">
                <div className="flex flex-col items-center gap-2 bg-brand-green-50 rounded-xl p-4 md:p-6">
                  <SkeletonLoader type="custom" className="w-24 h-24 md:w-28 md:h-28 rounded-full" />
                  <SkeletonLoader type="custom" className="h-4 w-16 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex gap-6 py-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonLoader key={i} type="custom" className="h-5 w-20 rounded" />
              ))}
            </div>
          </div>
        </div>
        <div className="container mx-auto px-0 sm:px-4 py-4 sm:py-6 md:py-8">
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 order-1">
              <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-4">
                <SkeletonLoader type="title" />
                <SkeletonLoader type="text" count={3} />
                <div className="grid grid-cols-2 gap-3 md:gap-4 mt-6">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="p-3 md:p-4 bg-gray-50 rounded-lg space-y-2">
                      <SkeletonLoader type="custom" className="h-3 w-20 rounded" />
                      <SkeletonLoader type="custom" className="h-7 w-16 rounded" />
                    </div>
                  ))}
                </div>
                <SkeletonLoader type="custom" className="h-24 w-full rounded-lg mt-4" />
              </div>
            </div>
            <div className="order-2">
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <SkeletonLoader type="custom" className="h-5 w-28 rounded" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <SkeletonLoader type="custom" className="h-5 w-5 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                      <SkeletonLoader type="custom" className="h-3 w-16 rounded" />
                    </div>
                  </div>
                ))}
                <SkeletonLoader type="button" className="w-full h-10 mt-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied UI
  if (accessDenied) {
    return (
      <div className="h-screen overflow-hidden  flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-[5rem]">
            {/* Top colored bar */}
            <div className="h-1.5 bg-gradient-to-r from-red-500 to-red-500" />

            <div className="p-6">
              {/* Icon */}
              <div className="relative mx-auto w-16 h-16 mb-4">
                <div className="absolute inset-0 bg-red-100 rounded-full animate-pulse" />
                <div className="relative w-full h-full bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center border-4 border-white shadow-lg">
                  <Shield className="w-7 h-7 text-red-500" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-xl font-bold text-gray-900 text-center mb-2">
                Access Restricted
              </h1>

              {/* Description */}
              <p className="text-gray-500 text-center text-sm mb-5">
                You don't have permission to view this learning path. It may not be assigned to you yet.
              </p>

              {/* Info Box */}
              <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-5">
                <div className="flex gap-2.5">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">
                    <span className="font-medium text-red-800">Need access?</span> Contact your administrator to request access.
                  </p>
                </div>
              </div>

              {/* Button */}
              <button
                onClick={() => navigate(isLearner ? '/learn/learning-paths' : '/admin/learning-paths')}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 font-medium text-sm shadow-md shadow-red-500/20"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Learning Paths
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while fetching data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Banner Skeleton */}
        <SkeletonLoader type="custom" className="w-full h-40 sm:h-56 md:h-80 lg:h-96 rounded-none" />

        {/* Title Section Skeleton */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8">
              <div className="flex-1 space-y-4">
                <SkeletonLoader type="custom" className="h-4 w-36 rounded" />
                <SkeletonLoader type="custom" className="h-8 w-3/4 rounded" />
                <div className="flex gap-4">
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                  <SkeletonLoader type="custom" className="h-4 w-20 rounded" />
                </div>
              </div>
              {/* Progress Circle Skeleton */}
              <div className="lg:flex-shrink-0">
                <div className="flex flex-col items-center gap-2 bg-brand-green-50 rounded-xl p-4 md:p-6">
                  <SkeletonLoader type="custom" className="w-24 h-24 md:w-28 md:h-28 rounded-full" />
                  <SkeletonLoader type="custom" className="h-4 w-16 rounded" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Skeleton */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4">
            <div className="flex gap-6 py-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonLoader key={i} type="custom" className="h-5 w-20 rounded" />
              ))}
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="container mx-auto px-0 sm:px-4 py-4 sm:py-6 md:py-8">
          <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 order-1">
              <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-4">
                <SkeletonLoader type="title" />
                <SkeletonLoader type="text" count={3} />
                {/* Progress Stats Grid */}
                <div className="grid grid-cols-2 gap-3 md:gap-4 mt-6">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="p-3 md:p-4 bg-gray-50 rounded-lg space-y-2">
                      <SkeletonLoader type="custom" className="h-3 w-20 rounded" />
                      <SkeletonLoader type="custom" className="h-7 w-16 rounded" />
                    </div>
                  ))}
                </div>
                <SkeletonLoader type="custom" className="h-24 w-full rounded-lg mt-4" />
              </div>
            </div>
            {/* Sidebar */}
            <div className="order-2">
              <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <SkeletonLoader type="custom" className="h-5 w-28 rounded" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <SkeletonLoader type="custom" className="h-5 w-5 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <SkeletonLoader type="custom" className="h-4 w-24 rounded" />
                      <SkeletonLoader type="custom" className="h-3 w-16 rounded" />
                    </div>
                  </div>
                ))}
                <SkeletonLoader type="button" className="w-full h-10 mt-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!learningPath) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">Learning path not found</p>
          <button
            onClick={() => navigate(isLearner ? '/learn/learning-paths' : '/admin/learning-paths')}
            className="px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const daysLeft = learningPath.deadline_date ? learnerLearningPathService.getDaysRemaining(learningPath.deadline_date) : null;
  const isOverdue = learningPath.deadline_date ? learnerLearningPathService.isOverdue(learningPath.deadline_date) : false;

  // Create a map of course progress by course_id for quick lookup
  const courseProgressMap = new Map<string, CourseProgress>();
  learningPath.course_progress?.forEach((progress) => {
    courseProgressMap.set(progress.course_id, progress);
  });

  // Handle course click
  const handleCourseClick = (course_id: string) => {
    const progress = courseProgressMap.get(course_id);
    if (progress) {
      // Navigate to AI study page (course player)
      navigate(`/learn/study/${course_id}`);
    } else {
      // Course not yet tracked, still allow navigation
      navigate(`/learn/study/${course_id}`);
    }
  };

  // Resume the path: open the first course that is NOT completed (in path order).
  // The old code picked the first course that had *any* progress entry, which was
  // always the first course — even when completed. Shared by the sidebar button
  // and the mobile button in the header (the sidebar sits at the bottom on mobile).
  const handleContinuePath = () => {
    if (!canWrite) return;
    const sortedCourses = [...(learningPath.courses || [])].sort(
      (a, b) => a.order - b.order
    );
    const nextCourse = sortedCourses.find((c) => {
      const progress = courseProgressMap.get(c.course_id);
      // Not started yet or in-progress — this is the one to resume.
      return !progress || progress.status !== 'COMPLETED';
    }) || sortedCourses.find((c) => courseProgressMap.has(c.course_id));
    if (nextCourse) {
      handleCourseClick(nextCourse.course_id);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner Image Section */}
      <div className="relative w-full h-40 sm:h-56 md:h-80 lg:h-96 bg-gradient-to-r from-brand-green-600 to-brand-emerald-600">
        {learningPath.banner_image_url || learningPath.thumbnail_image_url ? (
          <img
            src={learningPath.banner_image_url || learningPath.thumbnail_image_url}
            alt={learningPath.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="w-24 h-24 md:w-32 md:h-32 text-white/30" />
          </div>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Back Button */}
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={() => navigate(isLearner ? '/learn/learning-paths' : '/admin/learning-paths')}
            className="flex items-center gap-2 px-4 py-2 bg-black/30 backdrop-blur-sm text-white rounded-lg hover:bg-black/50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
            <span className="hidden sm:inline">Back to Learning Paths</span>
          </button>
        </div>

        {/* Status Badges (learner only) */}
        {isLearner && (learningPath.is_mandatory || learningPath.status) && (
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            {learningPath.is_mandatory && (
              <span className="bg-red-500 text-white text-xs md:text-sm px-3 py-1 rounded-full font-medium">
                Mandatory
              </span>
            )}
            <span className={`text-xs md:text-sm px-3 py-1 rounded-full font-medium ${
              learningPath.status === 'COMPLETED' ? 'bg-green-500 text-white' :
              learningPath.status === 'IN_PROGRESS' ? 'bg-blue-500 text-white' :
              'bg-gray-500 text-white'
            }`}>
              {learningPath.status.replace('_', ' ')}
            </span>
          </div>
        )}
      </div>

      {/* Title and Info Section */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8">
            {/* Left: Title and Badge */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-5 h-5 md:w-6 md:h-6 text-brand-green-600" />
                <span className="text-xs md:text-sm font-medium uppercase tracking-wider text-brand-green-600">Learning Path</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{learningPath.title}</h1>

              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 md:gap-6">
                <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                  <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600" />
                  <span>{learningPath.total_courses} courses</span>
                </div>
                {isLearner && (
                  <>
                    <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                      <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600" />
                      <span>{learningPath.completed_courses} completed</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm md:text-base text-gray-600">
                      <Clock className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600" />
                      <span>{learnerLearningPathService.formatTimeSpent(learningPath.total_time_spent_seconds)}</span>
                    </div>
                    {learningPath.deadline_date && (
                      <div className={`flex items-center gap-2 text-sm md:text-base ${
                        isOverdue ? 'text-red-600' : daysLeft && daysLeft <= 7 ? 'text-orange-600' : 'text-gray-600'
                      }`}>
                        <Calendar className="w-4 h-4 md:w-5 md:h-5" />
                        <span>
                          {isOverdue ? (
                            `${Math.abs(daysLeft!)}d overdue`
                          ) : daysLeft === 0 ? (
                            'Due today'
                          ) : (
                            `${daysLeft}d left`
                          )}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Right: Progress Circle (learner only) */}
            {isLearner && (
              <div className="lg:flex-shrink-0">
                {/* Mobile: one compact row (ring + summary + resume button) so the
                    learner sees where they are and how to continue without
                    scrolling past the tabs. Desktop keeps the stacked ring. */}
                <div className="flex flex-row lg:flex-col items-center gap-4 lg:gap-2 bg-brand-green-50 rounded-xl p-4 md:p-6">
                  <div className="relative w-20 h-20 md:w-28 md:h-28 flex-shrink-0">
                    <svg className="transform -rotate-90 w-full h-full" viewBox="0 0 100 100">
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        className="text-gray-200"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="transparent"
                        strokeDasharray={`${2 * Math.PI * 45}`}
                        strokeDashoffset={`${2 * Math.PI * 45 * (1 - learningPath.progress_percentage / 100)}`}
                        className="text-brand-green-600 transition-all duration-500"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl md:text-3xl font-bold text-gray-900">{learningPath.progress_percentage}%</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 lg:flex-none lg:text-center">
                    <span className="block text-gray-700 text-sm md:text-base font-medium">Progress</span>
                    <span className="block lg:hidden text-xs text-gray-500">
                      {learningPath.completed_courses} of {learningPath.total_courses} courses completed
                    </span>
                    {learningPath.status !== 'COMPLETED' && (
                      <button
                        onClick={handleContinuePath}
                        disabled={!canWrite}
                        title={!canWrite ? 'You do not have permission to start learning' : undefined}
                        className={`lg:hidden mt-2 w-full sm:w-auto px-4 py-2 bg-brand-green-600 text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-1 ${
                          canWrite ? 'hover:bg-brand-green-700' : 'opacity-50 cursor-not-allowed'
                        }`}
                      >
                        {learningPath.status === 'IN_PROGRESS' ? 'Continue Learning' : 'Start Learning'}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-[56px] lg:top-[64px] z-10 shadow-sm overflow-x-auto">
        <div className="container mx-auto px-4">
          <div className="flex gap-4 md:gap-8 min-w-max md:min-w-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-3 md:py-4 border-b-2 font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-brand-green-600 text-brand-green-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4 md:w-5 md:h-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-0 sm:px-4 py-4 sm:py-6 md:py-8">
        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 order-1">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-brand-green-600" />
                  About This Learning Path
                </h2>

                <div className="prose prose-sm md:prose-base max-w-none">
                  <p className="text-sm md:text-base text-gray-700">{learningPath.description || 'This learning path provides a structured journey through carefully curated courses designed to build your expertise.'}</p>
                </div>

                {/* Progress Stats */}
                <div className={`mt-6 md:mt-8 grid ${isLearner ? 'grid-cols-2' : 'grid-cols-1'} gap-3 md:gap-4`}>
                  <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs md:text-sm text-gray-600 mb-1">Total Courses</p>
                    <p className="text-xl md:text-2xl font-bold text-gray-900">{learningPath.total_courses}</p>
                  </div>
                  {isLearner && (
                    <>
                      <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Completed</p>
                        <p className="text-xl md:text-2xl font-bold text-gray-900">{learningPath.completed_courses}</p>
                      </div>
                      <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Time Spent</p>
                        <p className="text-lg md:text-xl font-bold text-gray-900">{learnerLearningPathService.formatTimeSpent(learningPath.total_time_spent_seconds)}</p>
                      </div>
                      <div className="p-3 md:p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs md:text-sm text-gray-600 mb-1">Progress</p>
                        <p className="text-xl md:text-2xl font-bold text-gray-900">{learningPath.progress_percentage}%</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-6 md:mt-8 p-4 md:p-6 bg-brand-green-50 rounded-lg border border-brand-green-200">
                  <h3 className="font-semibold text-brand-green-900 mb-2 flex items-center gap-2 text-sm md:text-base">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5" />
                    What You'll Achieve
                  </h3>
                  <p className="text-brand-green-800 text-sm md:text-base">
                    Upon completion, you'll have gained comprehensive knowledge and practical skills across all courses in this path.
                    This structured approach ensures deep understanding and mastery of the subject matter.
                  </p>
                </div>
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                  <FileText className="w-5 h-5 md:w-6 md:h-6 text-brand-green-600" />
                  Course Sequence
                </h2>
                <p className="text-sm md:text-base text-gray-600 mb-6">Complete courses in order to progress through this learning path</p>

                <div className="space-y-4">
                  {(() => {
                    if (isLearner) {
                      // Learner: Filter courses to only show those with progress data
                      const coursesWithProgress = learningPath.courses?.filter(
                        (courseInPath: CourseInPath) => courseProgressMap.has(courseInPath.course_id)
                      ) || [];

                      if (coursesWithProgress.length === 0) {
                        return (
                          <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 md:p-12 text-center">
                            <BookOpen className="w-12 h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">No Courses Assigned</h3>
                            <p className="text-sm md:text-base text-gray-600">No courses have been assigned to you in this learning path yet.</p>
                          </div>
                        );
                      }

                      return coursesWithProgress
                        .sort((a, b) => a.order - b.order)
                        .map((courseInPath: CourseInPath) => {
                          const courseProgress = courseProgressMap.get(courseInPath.course_id);
                          const isCompleted = courseProgress?.status === 'COMPLETED';
                          const isInProgress = courseProgress?.status === 'IN_PROGRESS';

                          return (
                            <div
                              key={courseInPath.course_id}
                              onClick={() => canWrite && handleCourseClick(courseInPath.course_id)}
                              className={`bg-white border-2 rounded-lg p-3 md:p-4 transition-all duration-200 ${canWrite ? 'cursor-pointer' : 'cursor-default'} ${
                                isCompleted ? 'border-green-200 bg-green-50/30' :
                                isInProgress ? 'border-green-300 hover:shadow-md hover:border-green-400' :
                                'border-gray-200 hover:shadow-md hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-start gap-3 md:gap-6">
                                {/* Order Number / Status Icon */}
                                <div className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-base md:text-lg ${
                                  isCompleted ? 'bg-green-500 text-white' :
                                  isInProgress ? 'bg-green-500 text-white' :
                                  'bg-gray-300 text-gray-600'
                                }`}>
                                  {isCompleted ? (
                                    <CheckCircle className="w-5 h-5 md:w-6 md:h-6" />
                                  ) : (
                                    courseInPath.order
                                  )}
                                </div>

                                {/* Course Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-3">
                                    <div className="flex-1 min-w-0">
                                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1">
                                        {courseInPath.course_title}
                                      </h3>
                                      {courseInPath.course_description && (
                                        <p className="text-xs md:text-sm text-gray-600 mb-2 line-clamp-2">
                                          {courseInPath.course_description}
                                        </p>
                                      )}
                                      {courseInPath.rationale && (
                                        <p className="text-xs text-gray-500 italic mb-2">
                                          💡 {courseInPath.rationale}
                                        </p>
                                      )}
                                      <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-600">
                                        {courseInPath.difficulty_level && (
                                          <span className="capitalize">{courseInPath.difficulty_level}</span>
                                        )}
                                        {courseProgress && (
                                          <>
                                            <span className="hidden sm:inline">•</span>
                                            <span>{courseProgress.completed_modules}/{courseProgress.total_modules} modules</span>
                                            <span className="hidden sm:inline">•</span>
                                            <span className="hidden sm:inline">{learnerLearningPathService.formatTimeSpent(courseProgress.time_spent_seconds)}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    {/* Status Badge */}
                                    <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium flex-shrink-0 self-start ${
                                      isCompleted ? 'bg-green-100 text-green-700' :
                                      isInProgress ? 'bg-blue-100 text-blue-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {isCompleted ? 'Completed' :
                                       isInProgress ? 'In Progress' :
                                       'Not Started'}
                                    </span>
                                  </div>

                                  {/* Progress Bar */}
                                  {courseProgress && !isCompleted && (
                                    <div className="mb-4">
                                      <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-gray-600">Course Progress</span>
                                        <span className="text-xs font-semibold text-gray-900">{courseProgress.progress_percentage}%</span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                          className={`h-2 rounded-full transition-all duration-300 ${
                                            isInProgress ? 'bg-brand-green-600' : 'bg-gray-400'
                                          }`}
                                          style={{ width: `${courseProgress.progress_percentage}%` }}
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {/* Action Button */}
                                  <button
                                    className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 sm:py-2 rounded-lg font-medium transition-colors ${
                                      !canWrite ? 'opacity-50 cursor-not-allowed' :
                                      isCompleted ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                                      isInProgress ? 'bg-brand-green-600 text-white hover:bg-brand-green-700' :
                                      'bg-gray-600 text-white hover:bg-gray-700'
                                    } ${!canWrite && (isCompleted ? 'bg-green-100 text-green-700' : isInProgress ? 'bg-brand-green-600 text-white' : 'bg-gray-600 text-white')}`}
                                    disabled={!canWrite}
                                    title={!canWrite ? 'You do not have permission to start courses' : undefined}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (canWrite) handleCourseClick(courseInPath.course_id);
                                    }}
                                  >
                                    {isCompleted ? (
                                      <>
                                        <CheckCircle className="w-4 h-4" />
                                        <span>Review Course</span>
                                      </>
                                    ) : isInProgress ? (
                                      <>
                                        <Play className="w-4 h-4" />
                                        <span>Continue Learning</span>
                                      </>
                                    ) : (
                                      <>
                                        <Play className="w-4 h-4" />
                                        <span>Start Course</span>
                                      </>
                                    )}
                                    <ChevronRight className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        });
                    } else {
                      // Admin: Show all courses without tracking data
                      const allCourses = learningPath.courses || [];

                      if (allCourses.length === 0) {
                        return (
                          <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 md:p-12 text-center">
                            <BookOpen className="w-12 h-12 md:w-16 md:h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">No Courses Added</h3>
                            <p className="text-sm md:text-base text-gray-600">No courses have been added to this learning path yet.</p>
                          </div>
                        );
                      }

                      return allCourses
                        .sort((a, b) => a.order - b.order)
                        .map((courseInPath: CourseInPath) => (
                          <div
                            key={courseInPath.course_id}
                            className="bg-white border-2 border-gray-200 rounded-lg p-3 md:p-4"
                          >
                            <div className="flex items-start gap-3 md:gap-6">
                              {/* Order Number */}
                              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-bold text-base md:text-lg bg-brand-green-100 text-brand-green-700">
                                {courseInPath.order}
                              </div>

                              {/* Course Content */}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-1">
                                  {courseInPath.course_title}
                                </h3>
                                {courseInPath.course_description && (
                                  <p className="text-xs md:text-sm text-gray-600 mb-2 line-clamp-2">
                                    {courseInPath.course_description}
                                  </p>
                                )}
                                {courseInPath.rationale && (
                                  <p className="text-xs text-gray-500 italic mb-2">
                                    💡 {courseInPath.rationale}
                                  </p>
                                )}
                                {courseInPath.difficulty_level && (
                                  <span className="text-xs md:text-sm text-gray-600 capitalize">{courseInPath.difficulty_level}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ));
                    }
                  })()}
                </div>
              </div>
            )}

            {/* Outcomes Tab */}
            {activeTab === 'outcomes' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                  <Target className="w-5 h-5 md:w-6 md:h-6 text-brand-green-600" />
                  Learning Outcomes & Tags
                </h2>

                <div className="space-y-6">
                  {/* Learning Outcomes */}
                  {learningPath.outcomes && learningPath.outcomes.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-base md:text-lg mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                        What You'll Learn
                      </h3>
                      <ul className="space-y-2">
                        {learningPath.outcomes.map((outcome, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm md:text-base text-gray-700">{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tags */}
                  {learningPath.tags && learningPath.tags.length > 0 && (
                    <div className="pt-6 border-t">
                      <h3 className="font-semibold text-base md:text-lg mb-3">Topics Covered</h3>
                      <div className="flex flex-wrap gap-2">
                        {learningPath.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1.5 bg-brand-green-100 text-brand-green-700 rounded-full text-xs md:text-sm font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {(!learningPath.outcomes || learningPath.outcomes.length === 0) &&
                   (!learningPath.tags || learningPath.tags.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <Target className="w-12 h-12 md:w-16 md:h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-sm md:text-base">No outcomes or tags available for this learning path.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:p-8">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2">
                  <Star className="w-5 h-5 md:w-6 md:h-6 text-yellow-500" />
                  Reviews
                </h2>
                <ReviewsList
                  targetId={learning_path_id || ''}
                  targetType={ReviewType.LEARNING_PATH}
                  targetTitle={learningPath?.title || ''}
                  showWriteReview={isLearner}
                  canWriteReview={canWrite}
                  currentUserId={user?.id}
                  isAdmin={!isLearner}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="order-2">
            {/* Learning Path Summary Card */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 lg:sticky lg:top-24">
              <h3 className="font-semibold text-base md:text-lg mb-4">Path Summary</h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm md:text-base text-gray-900">Total Courses</p>
                    <p className="text-xs md:text-sm text-gray-600">{learningPath.total_courses} courses</p>
                  </div>
                </div>

                {isLearner && (
                  <>
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-sm md:text-base text-gray-900">Completed</p>
                        <p className="text-xs md:text-sm text-gray-600">{learningPath.completed_courses} of {learningPath.total_courses}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-sm md:text-base text-gray-900">Time Spent</p>
                        <p className="text-xs md:text-sm text-gray-600">{learnerLearningPathService.formatTimeSpent(learningPath.total_time_spent_seconds)}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Target className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-sm md:text-base text-gray-900">Progress</p>
                        <p className="text-xs md:text-sm text-gray-600">{learningPath.progress_percentage}% complete</p>
                      </div>
                    </div>

                    {learningPath.deadline_date && (
                      <div className="flex items-start gap-3">
                        <Calendar className="w-4 h-4 md:w-5 md:h-5 text-brand-green-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm md:text-base text-gray-900">Deadline</p>
                          <p className={`text-xs md:text-sm ${
                            isOverdue ? 'text-red-600' : daysLeft && daysLeft <= 7 ? 'text-orange-600' : 'text-gray-600'
                          }`}>
                            {learningPath.deadline_date ? (
                              isOverdue ? (
                                `${Math.abs(daysLeft!)}d overdue`
                              ) : daysLeft === 0 ? (
                                'Due today'
                              ) : (
                                `${daysLeft}d left`
                              )
                            ) : (
                              'No deadline'
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {isLearner && learningPath.status !== 'COMPLETED' && (
                <div className="mt-6 pt-6 border-t">
                  <button
                    onClick={handleContinuePath}
                    disabled={!canWrite}
                    title={!canWrite ? 'You do not have permission to start learning' : undefined}
                    className={`w-full px-4 md:px-6 py-2 md:py-3 bg-brand-green-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all text-sm md:text-base ${
                      canWrite ? 'hover:bg-brand-green-700' : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {learningPath.status === 'IN_PROGRESS' ? 'Continue Learning' : 'Start Learning'}
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </div>
              )}

              {isLearner && learningPath.status === 'COMPLETED' && (
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center justify-center gap-2 text-green-600 font-medium text-sm md:text-base">
                    <CheckCircle className="w-5 h-5" />
                    <span>Completed!</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
